import React from 'react';
import useOrganizationMedicalProfiles from '../../../../hooks/api/useOrganizationMedicalProfiles';
import { AnimateLoadedContent } from '../../../v2BaseComponents/Loader';
import LabelValueDisplay from '../../../v2BaseComponents/LabelValueDisplay';
import { getIntl } from '../../../../utils/intl';
import useMedicalProfileOwner from '../../../../hooks/api/useMedicalProfileOwner';
import Stack, { contentStackSpacing } from '../../../v2BaseComponents/Stack';
import { LoadableAvatar, getFallbackFromName } from '../../../v2BaseComponents/Avatar';
import urls from '../../../../utils/urls';
import getName from '../../../../utils/getName';
import getDateString from '../../../../utils/getDateString';
import addressToString from '../../../../utils/addressToString';
import { useOrganizationClientRecords } from '../../../../hooks/api/useOrganizationClientRecords';

const intl = getIntl('client-profile-details', {
    name: 'Name',
    groups: 'Groups',
    dob: 'Date of Birth',
    sex: 'Sex',
    gender: 'Gender',
    address: 'Address',
    email: 'Email',
    phone: 'Phone',
    fax: 'Fax',
    profileOrganizer: 'Profile Organizer',
    profileOrganizerEmail: 'Profile Organizer Email',
});

export type ClientProfileDetailsOwnProps = {
    clientProfileId: string;
    clientRecordId?: string;
};

export type ClientProfileDetailsProps = ClientProfileDetailsOwnProps;

const ClientProfileDetails: React.FC<ClientProfileDetailsProps> = (props) => {
    const { isLoadingOrganizationMedicalProfile, organizationMedicalProfile } = useOrganizationMedicalProfiles({
        medicalProfileId: props.clientProfileId,
    });
    const medicalProfileOwner = useMedicalProfileOwner({
        medicalProfileId: props.clientProfileId,
    });
    const { isLoadingClientRecordGroups, clientRecordGroups } = useOrganizationClientRecords({
        organizationClientRecordId: props.clientRecordId,
    });

    return (
        <AnimateLoadedContent
            isLoading={
                isLoadingOrganizationMedicalProfile || isLoadingClientRecordGroups || medicalProfileOwner.isLoading
            }
        >
            {() => (
                <Stack vertical fullWidth align="center" space={contentStackSpacing}>
                    <LoadableAvatar
                        avatarUrl={urls.api.medicalProfiles.avatar(props.clientProfileId)}
                        size="xl"
                        fallbackName={getFallbackFromName(
                            organizationMedicalProfile!.contactDetails.firstName,
                            organizationMedicalProfile!.contactDetails.lastName,
                        )}
                    />
                    <LabelValueDisplay
                        omitEmptyValues
                        items={[
                            { label: intl('name'), value: getName(organizationMedicalProfile!.contactDetails) },
                            {
                                label: intl('groups'),
                                value: clientRecordGroups
                                    .map((group) => group.metadata.name)
                                    .sort((a, b) => a.localeCompare(b))
                                    .join(', '),
                            },
                            {
                                label: intl('dob'),
                                value: getDateString(organizationMedicalProfile!.contactDetails.dob, 'date'),
                            },
                            {
                                label: intl('sex'),
                                value: organizationMedicalProfile!.contactDetails.sex,
                            },
                            {
                                label: intl('gender'),
                                value: organizationMedicalProfile!.contactDetails.gender,
                            },
                            {
                                label: intl('address'),
                                value: addressToString(organizationMedicalProfile!.contactDetails),
                            },
                            {
                                label: intl('email'),
                                value: organizationMedicalProfile!.contactDetails.email,
                                linkType: 'email',
                            },
                            {
                                label: intl('phone'),
                                value: organizationMedicalProfile!.contactDetails.phoneNumber,
                                linkType: 'phone',
                            },
                            {
                                label: intl('fax'),
                                value: organizationMedicalProfile!.contactDetails.faxNumber,
                            },
                            {
                                label: intl('profileOrganizer'),
                                value: medicalProfileOwner.owner ? getName(medicalProfileOwner.owner) : '',
                            },
                            {
                                label: intl('profileOrganizerEmail'),
                                value: medicalProfileOwner.owner ? medicalProfileOwner.owner.email : '',
                                linkType: 'email',
                            },
                        ]}
                    />
                </Stack>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(ClientProfileDetails);
